import { Button, Card, Pill, Stack, Text } from '@/design-system'
import { useCopy } from '@/lib/use-copy'
import { QrCode } from '@/components/QrCode'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const MANUAL = [
  ['SM-DP+ address', 'rsp.truphone.com'],
  ['Activation code', 'K2-9PLM-4471-QZ8'],
  ['ICCID', '8944 5000 0000 4471 902'],
] as const

const STEPS = [
  ['Open Settings', 'Go to Cellular, then Add eSIM.'],
  ['Scan this code', 'Point the camera at the square on the left.'],
  ['Label it', 'Call it Travel so you can tell it apart from your usual line.'],
  ['Leave data off until you land', 'It switches itself on when you arrive.'],
] as const

export function EsimQrScreen() {
  const [copied, copy] = useCopy()

  return (
    <>
      <PageHeader
        title="Install your eSIM"
        subtitle="5 GB · Mexico · Telcel. Install it now over Wi-Fi — you cannot scan this once you have landed without data."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/esim">
            Back to eSIM
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                Your QR code
              </Text>
              <Pill tone="accent">Ready</Pill>
            </Card.Header>
            <Stack align="center" gap={4} className="pb-mt-4">
              <QrCode value="LPA:1$rsp.truphone.com$K2-9PLM-4471-QZ8" />
              <Text variant="eyebrow" tone="onDarkMuted">
                Valid for 30 days from activation
              </Text>
            </Stack>

            <div className={checkout.rule} />

            {/* An ICCID is too long to sit beside its label on a phone, and it is
                read digit by digit, so it gets its own line at full width. */}
            <Stack gap={4}>
              {MANUAL.map(([label, value]) => (
                <Stack key={label} gap={1} align="start">
                  <Text variant="eyebrow" tone="onDarkMuted">
                    {label}
                  </Text>
                  <Text
                    variant="caption"
                    weight="semibold"
                    tone="onDark"
                    numeric
                    style={{ fontFamily: 'var(--pb-font-mono)', wordBreak: 'break-word' }}
                  >
                    {value}
                  </Text>
                </Stack>
              ))}
            </Stack>

            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button
                  size="md"
                  iconStart={copied ? 'check' : 'content_copy'}
                  onClick={() => copy(MANUAL.map(([label, value]) => `${label}: ${value}`).join('\n'))}
                >
                  {copied ? 'Copied' : 'Copy details'}
                </Button>
                <Button variant="onDark" size="md" iconStart="ios_share">
                  Send to my phone
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              How to install it
            </Text>
            <Stack gap={0} className="pb-mt-3">
              {STEPS.map(([title, body], index) => (
                <div key={title} className={checkout.line}>
                  <Card tone="dark" padded={false} className="pb-coin">
                    <Text variant="eyebrow" weight="bold" tone="accent">
                      {index + 1}
                    </Text>
                  </Card>
                  <Stack grow gap={0}>
                    <Text variant="caption" weight="semibold">
                      {title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {body}
                    </Text>
                  </Stack>
                </div>
              ))}
            </Stack>
            <Card.Footer>
              <Button variant="outline" size="md" block iconEnd="arrow_forward" to="/esim/buy">
                Add more data
              </Button>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
